import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { ga4Track, toggleInteraction } from '@unisporkal/ga4';
import { useTranslations } from '@unisporkal/localization';
import { useTheme } from '@unisporkal/themes';
import assetSizeLabel from '../../../../support/assetSizeLabel';
import { imageSizeType } from '../../../../types/sizeType';

import PriceInfo from '../PriceInfo/PriceInfo.tsx';
import styles from '../AssetSizes.module.scss';
import useSelectedIndex from '../../../../hooks/stateful/useSelectedIndex.ts';

const ImageSize = ({ size, index, singleView = false }) => {
  const t = useTranslations();
  const theme = useTheme();
  const themeRadio = theme.radio || {};

  const { selectedSizeIndex, setSelectedSizeIndex } = useSelectedIndex();
  const isSelected = selectedSizeIndex === index;

  const trackClick = () => {
    const ga4Event = toggleInteraction({
      toggle_interaction: 'rf_toggle',
      toggle_selection: `${size.label.toLowerCase()}_size_image`,
      ui_element_location: 'acquisition_card_options',
    });

    ga4Track(ga4Event);
  };

  const handleClick = () => {
    setSelectedSizeIndex(index);
    trackClick();
  };

  const showPixelSizes = () =>
    size.pixelWidth && size.pixelHeight
      ? t('img_size_in_pixels', {
          width: size.pixelWidth,
          height: size.pixelHeight,
        })
      : '';

  const showUnitSizes = () =>
    size.localeWidth && size.localeHeight && size.unit
      ? t('img_size_in_units', {
          width: size.localeWidth,
          height: size.localeHeight,
          units: t(`${size.unit}`),
        })
      : '';

  const showMegapixelSizes = () => {
    if (size.megapixels) {
      return t('img_size_in_megapixels', { size: size.megapixels });
    }
    return '';
  };

  const firstSizeInfo = () => {
    const pixelSizes = showPixelSizes(size);
    const unitSizes = showUnitSizes(size);
    return pixelSizes && unitSizes ? `${pixelSizes} (${unitSizes})` : '';
  };

  const secondSizeInfo = () => {
    const dpiSize = size.dpi ? size.dpi : '';
    const megapixelSizes = showMegapixelSizes(size);
    return dpiSize && megapixelSizes
      ? `${dpiSize} dpi | ${megapixelSizes}`
      : '';
  };

  const sizeLabelStyleName = () => {
    if (singleView) {
      return styles.singleSizeItem;
    }
    if (isSelected) {
      return styles.selectedSizeItem;
    }
    return styles.sizeItem;
  };

  return (
    <label className={sizeLabelStyleName()}>
      <div
        className={styles.itemDetails}
        data-testid="image-size"
      >
        {!singleView && (
          <input
            className={cx(themeRadio.radioButton, styles.radio)}
            type="radio"
            checked={isSelected}
            id={size.label}
            onChange={handleClick}
            data-testid={`image-size-${size.label}`}
          />
        )}
        <div className={styles.description}>
          <span className={styles.assetSizeLabel}>
            {assetSizeLabel(t, size)}
          </span>
          {(isSelected || singleView) && (
            <div className={styles.sizeDetails}>
              <div className={styles.info}>{firstSizeInfo()}</div>
              <div className={styles.info}>{secondSizeInfo()}</div>
            </div>
          )}
        </div>
      </div>
      <PriceInfo
        size={size}
        currencyCode={size.currencyCode}
      />
    </label>
  );
};

ImageSize.propTypes = {
  size: imageSizeType.isRequired,
  index: PropTypes.number.isRequired,
  singleView: PropTypes.bool,
};

export default ImageSize;
